import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { API } from "@/auth/AuthContext";

const IMG = {
  home: "https://images.unsplash.com/photo-1558002038-1055907df827?q=85&w=1200&auto=format&fit=crop",
  office: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=85&w=1200&auto=format&fit=crop",
  workstation: "https://images.unsplash.com/photo-1587202372634-32705e3bf49c?q=85&w=1200&auto=format&fit=crop",
  datacenter: "https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const CUSTOMER_TYPES = ["Residential Home", "Small / Medium Business", "Commercial Property", "Enterprise Organization"];
const AI_SERVICES = [
  "Intelligent Home AI", "Smart Home AI Integration", "Private Home AI Assistant",
  "Business AI Assistant", "Business Process Automation", "Intelligent Office Automation",
  "AI Workstation Installation", "Local AI Server Deployment", "Enterprise AI Infrastructure",
  "AI Infrastructure Consultation",
];

const inputCls =
  "w-full bg-[#0a0a0c] border border-zinc-700 rounded p-3 text-white text-xs font-mono focus:border-[#d4af37] outline-none transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5";

function FeatureList({ items }) {
  return (
    <ul className="space-y-2 text-xs text-zinc-300 font-mono">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-2"><span className="text-[#d4af37] mt-0.5">▶</span> {i}</li>
      ))}
    </ul>
  );
}

function TierSection({ id, badge, title, tagline, desc, image, alt, blocks, primary, onExplore }) {
  return (
    <section id={id} className="py-16 border-t border-[#d4af37]/10" data-testid={`tier-${id}`}>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div className="rounded-xl overflow-hidden gold-border aspect-[4/3] bg-[#121216] relative">
          <img src={image} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20"></div>
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded bg-black/70 text-[10px] font-mono border border-[#d4af37]/30 text-[#d4af37] uppercase">{badge}</span>
        </div>
        <div>
          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white">{title}</h2>
          <p className="font-display text-sm uppercase tracking-widest text-[#d4af37] mt-2">{tagline}</p>
          <p className="text-sm text-zinc-400 leading-relaxed mt-4">{desc}</p>
          <div className="space-y-6 mt-6">
            {blocks.map((b) => (
              <div key={b.title}>
                <h3 className="text-sm font-semibold text-white uppercase tracking-wide mb-2">{b.title}</h3>
                {b.text && <p className="text-xs text-zinc-400 leading-relaxed mb-2">{b.text}</p>}
                {b.items && <FeatureList items={b.items} />}
                {b.examples && (
                  <div className="space-y-1.5 mt-2">
                    {b.examples.map((ex) => (
                      <p key={ex} className="text-xs font-mono text-[#d4af37]/90 bg-[#0a0a0c] border border-[#d4af37]/20 rounded px-3 py-2">"{ex}"</p>
                    ))}
                  </div>
                )}
                {b.note && <p className="text-[10px] text-zinc-500 font-mono mt-2 leading-relaxed">{b.note}</p>}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <button type="button" onClick={() => onExplore(primary.service)} data-testid={`explore-${id}-btn`}
              className="px-6 py-3 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity">
              {primary.label}
            </button>
            <button type="button" onClick={() => onExplore(primary.service)} data-testid={`quote-${id}-btn`}
              className="px-6 py-3 rounded border border-[#d4af37]/40 text-[#d4af37] font-bold uppercase tracking-wider text-xs hover:bg-[#d4af37] hover:text-black transition-colors">
              {primary.secondary}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function InquiryForm({ service, setService }) {
  const EMPTY = {
    name: "", phone: "", email: "", project_address: "", customer_type: CUSTOMER_TYPES[0],
    ai_service: service, existing_equipment: "", description: "", budget: "", preferred_date: "",
  };
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications/local-ai`, { ...form, ai_service: service });
      setDone(true);
      toast.success("Inquiry received — DuroTech28 will reach out shortly.");
    } catch {
      toast.error("Could not submit right now. Please call 469-897-6497.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="ai-inquiry-success">
        <div className="w-14 h-14 mx-auto mb-5 rounded-full border border-[#d4af37]/50 flex items-center justify-center">
          <svg className="w-7 h-7 text-[#d4af37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <p className="text-sm text-zinc-200 leading-relaxed max-w-md mx-auto">
          Thank you for your interest in DuroTech28's Local AI services. Your inquiry has been received —
          our team will review your requirements and contact you to schedule a consultation.
        </p>
        <button type="button" onClick={() => { setDone(false); setForm(EMPTY); }} data-testid="ai-inquiry-reset"
          className="mt-6 px-6 py-2.5 rounded border border-[#d4af37]/40 text-[10px] font-mono uppercase tracking-wider text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors">
          Submit Another Inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ai-inquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Customer Name *</label>
          <input required value={form.name} onChange={set("name")} data-testid="ai-name" className={inputCls} /></div>
        <div><label className={labelCls}>Phone *</label>
          <input required value={form.phone} onChange={set("phone")} data-testid="ai-phone" className={inputCls} /></div>
        <div><label className={labelCls}>Email *</label>
          <input required type="email" value={form.email} onChange={set("email")} data-testid="ai-email" className={inputCls} /></div>
        <div><label className={labelCls}>Project Address</label>
          <input value={form.project_address} onChange={set("project_address")} placeholder="City / address" data-testid="ai-address" className={inputCls} /></div>
        <div><label className={labelCls}>Customer Type</label>
          <select value={form.customer_type} onChange={set("customer_type")} data-testid="ai-customer-type" className={inputCls}>
            {CUSTOMER_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select></div>
        <div><label className={labelCls}>Requested AI Service</label>
          <select value={service} onChange={(e) => setService(e.target.value)} data-testid="ai-service-select" className={inputCls}>
            {AI_SERVICES.map((s) => <option key={s}>{s}</option>)}
          </select></div>
      </div>
      <div><label className={labelCls}>Existing Equipment / Systems</label>
        <input value={form.existing_equipment} onChange={set("existing_equipment")} placeholder="e.g. smart doorbell, cameras, office network..." data-testid="ai-equipment" className={inputCls} /></div>
      <div><label className={labelCls}>Project Description</label>
        <textarea rows="4" value={form.description} onChange={set("description")} placeholder="What would you like your intelligent system to do?" data-testid="ai-description" className={inputCls}></textarea></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Estimated Budget (optional)</label>
          <input value={form.budget} onChange={set("budget")} data-testid="ai-budget" className={inputCls} /></div>
        <div><label className={labelCls}>Preferred Consultation Date</label>
          <input value={form.preferred_date} onChange={set("preferred_date")} data-testid="ai-date" className={inputCls} /></div>
      </div>
      <button type="submit" disabled={submitting} data-testid="ai-inquiry-submit"
        className="w-full py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity shadow-lg shadow-[#d4af37]/25 disabled:opacity-60">
        {submitting ? "Submitting..." : "Request AI Consultation"}
      </button>
      <p className="text-[10px] text-zinc-500 font-mono text-center leading-relaxed">
        All capabilities are evaluated based on your existing equipment, network, budget, and technical requirements.
      </p>
    </form>
  );
}

export default function LocalAIPage() {
  const [service, setService] = useState(AI_SERVICES[0]);
  const onExplore = (svc) => {
    setService(svc);
    document.querySelector("#inquiry")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Seo
        title="Local AI & Smart Automation | DuroTech28 — DFW"
        description="Privacy-first local AI for homes and businesses in Dallas–Fort Worth: intelligent automation, AI assistants and on-premise hardware installed by DuroTech28."
        canonical="https://durotech28.me/local-ai"
      />
      <Nav />
      <main data-testid="local-ai-page">
        <header className="max-w-5xl mx-auto px-6 pt-16 pb-14 text-center honeycomb">
          <span className="text-xs uppercase tracking-widest text-[#d4af37] font-mono">Local AI &amp; Intelligent Automation</span>
          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tight text-white mt-4 leading-[0.98]">
            Beyond Smart.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] via-[#fff4cc] to-[#c0c0c0]">Truly Intelligent.</span>
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mt-6">
            Transform your home, business, or enterprise with private AI systems designed to connect,
            understand, and automate your technology.
          </p>
          <p className="text-xs text-zinc-500 max-w-2xl mx-auto leading-relaxed mt-6 font-mono">
            Traditional smart technology follows commands. Intelligent technology can understand context,
            coordinate connected systems, and automate complex tasks based on your preferences. DuroTech28
            helps customers move beyond individual smart devices toward integrated, AI-powered environments —
            with an approach that prioritizes privacy, reliability, security, and customer control.
          </p>
        </header>

        <div className="max-w-6xl mx-auto px-6">
          <TierSection
            id="home" badge="Residential" title="Intelligent Home AI"
            tagline="Turn your smart home into a truly intelligent home."
            desc="Most smart homes rely on individual apps, voice assistants, and disconnected devices. DuroTech28 brings these technologies together through a centralized, intelligent automation system — so instead of managing every device separately, you interact with a connected system designed around your lifestyle."
            image={IMG.home} alt="Modern connected living space with intelligent home automation"
            blocks={[
              { title: "AI Home Assistant", text: "A locally hosted AI assistant that can interpret natural-language requests and interact with compatible home systems.",
                examples: ["Turn off everything downstairs.", "Prepare the house for bedtime.", "Set the living room for movie night.", "Adjust the lights and temperature for the evening."] },
              { title: "Intelligent Lighting", items: ["Coordinate compatible lighting based on schedules, occupancy, preferences, and custom routines."] },
              { title: "Intelligent Climate Control", items: ["Time of day", "Room occupancy", "Temperature", "User preferences", "Energy-saving schedules"] },
              { title: "AI-Assisted Home Security", items: ["Smart notifications", "Event summaries", "Camera event classification", "Security system coordination", "Custom alerts"],
                note: "Security-critical actions always include appropriate safeguards and user controls." },
              { title: "Home Entertainment Automation", items: ["Televisions, audio systems, streaming devices, smart lighting, and home theater equipment — personalized scenes and routines."] },
              { title: "Household Automation", items: ["Morning & bedtime routines", "Arrival and departure", "Smart appliance coordination", "Household reminders", "Energy management"] },
              { title: "Privacy-First Design", text: "Where technically feasible, AI processing can run locally on customer-owned hardware, reducing dependence on external cloud services. Some smart devices and AI functions may still require internet connectivity." },
            ]}
            primary={{ label: "Explore Intelligent Home AI", secondary: "Request Custom Quote", service: "Intelligent Home AI" }}
            onExplore={onExplore}
          />

          <TierSection
            id="business" badge="Business" title="Intelligent Business AI"
            tagline="Automate operations. Empower your business."
            desc="DuroTech28 helps businesses explore private AI systems and intelligent automation solutions designed to improve productivity and streamline daily operations — integrated systems that coordinate information, devices, and workflows instead of disconnected software tools."
            image={IMG.office} alt="Modern smart office environment"
            blocks={[
              { title: "AI Business Assistants", items: ["Internal company information", "Document retrieval", "Employee knowledge support", "Business process assistance", "Customer inquiry support"] },
              { title: "AI Customer Communication", items: ["Website inquiries", "Appointment requests", "Customer support workflows", "Email categorization", "Follow-up reminders", "CRM workflows"],
                note: "Automated communications follow customer preferences and applicable privacy and consent requirements." },
              { title: "Intelligent Office Automation", items: ["Office lighting", "Smart thermostats", "Access control systems", "Meeting room equipment", "Security systems", "Occupancy sensors"] },
              { title: "Business Workflow Automation", items: ["Document organization", "Invoice processing assistance", "Scheduling workflows", "Internal reporting", "Inventory notifications", "Task management"] },
              { title: "Private Business AI", text: "Deploy compatible AI models on business-owned computers or servers — greater control over business data, customizable workflows, reduced reliance on some cloud services, and integration with internal documents. Security and privacy depend on proper system design and configuration." },
            ]}
            primary={{ label: "Explore Business AI", secondary: "Request Business Consultation", service: "Business AI Assistant" }}
            onExplore={onExplore}
          />

          <TierSection
            id="enterprise" badge="Enterprise" title="Enterprise AI Infrastructure"
            tagline="Intelligence at scale."
            desc="DuroTech28's enterprise AI services focus on infrastructure planning, deployment, integration, and support for organizations exploring locally hosted artificial intelligence — combining experience in structured cabling, fiber optics, networking, and data center infrastructure with AI computing and automation solutions."
            image={IMG.datacenter} alt="Enterprise server and data center infrastructure"
            blocks={[
              { title: "AI Server Deployment", items: ["GPU server installation", "Hardware configuration", "Rack installation", "Network connectivity", "Storage integration", "Infrastructure testing"] },
              { title: "Private AI Infrastructure", items: ["On-premises AI systems", "Private model deployment", "Internal AI assistants", "Enterprise document retrieval", "Local inference infrastructure"] },
              { title: "Intelligent Building Systems", items: ["Building automation", "Environmental monitoring", "Occupancy-based automation", "Equipment status monitoring", "Energy management assistance"] },
              { title: "Data Center AI Infrastructure", items: ["Fiber optic infrastructure", "Structured cabling", "Rack and equipment installation", "Server connectivity", "AI hardware deployment support"] },
              { title: "Enterprise Automation", items: ["IT workflow automation", "Infrastructure monitoring", "Internal knowledge systems", "Operational reporting", "AI-assisted troubleshooting"],
                note: "All enterprise projects require technical evaluation and a custom scope of work, with human oversight and appropriate security safeguards." },
            ]}
            primary={{ label: "Explore Enterprise AI", secondary: "Request Enterprise Quote", service: "Enterprise AI Infrastructure" }}
            onExplore={onExplore}
          />

          {/* Hardware */}
          <section className="py-16 border-t border-[#d4af37]/10" data-testid="ai-hardware">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[#d4af37]">Infrastructure Options</span>
              <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white mt-2">The Technology Behind the Intelligence</h2>
              <p className="text-xs text-zinc-500 mt-3">Local AI can operate on different types of hardware depending on project requirements. Every recommendation is based on your specific needs.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                ["AI-Ready Mini Computers", "Residential automation and lightweight AI tasks."],
                ["Custom AI Workstations", "GPU-equipped systems designed for compatible AI applications."],
                ["Dedicated AI Servers", "Higher-capacity systems for business and commercial workloads."],
                ["Enterprise GPU Infrastructure", "Scalable computing infrastructure for demanding AI workloads."],
                ["Networking & Storage", "Secure communication, data access, and connected automation systems."],
              ].map(([t, d], i) => (
                <div key={t} className="rounded-xl bg-[#121216] gold-border p-5" data-testid={`hw-${i}`}>
                  <span className="font-display text-2xl text-[#d4af37]/40">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-sm font-semibold text-white mt-3 leading-snug">{t}</h3>
                  <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Packages */}
          <section className="py-16 border-t border-[#d4af37]/10" data-testid="ai-packages">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white">AI Service Packages</h2>
              <p className="text-xs text-zinc-500 mt-3 font-mono">All packages require consultation and a customized project estimate.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { name: "Duro Intelligent Home", sub: "Residential AI and smart home integration", svc: "Intelligent Home AI",
                  items: ["Smart home consultation", "Device compatibility assessment", "Local AI hardware planning", "AI assistant configuration", "Smart device integration", "Custom automation routines"] },
                { name: "Duro Intelligent Business", sub: "Business AI and intelligent automation", svc: "Business AI Assistant",
                  items: ["Business workflow assessment", "Private AI assistant setup", "Office automation", "Document intelligence", "Software integration", "AI workstation or server deployment"] },
                { name: "Duro Enterprise AI", sub: "Enterprise AI infrastructure and systems integration", svc: "Enterprise AI Infrastructure",
                  items: ["Infrastructure consultation", "GPU server deployment", "Private AI infrastructure", "Enterprise networking", "Data center infrastructure support", "Custom automation integration"] },
              ].map((p) => (
                <div key={p.name} className="rounded-2xl p-7 bg-[#121216] gold-border flex flex-col" data-testid={`pkg-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h3 className="font-display text-xl uppercase text-white">{p.name}</h3>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mt-1 mb-5">{p.sub}</p>
                  <FeatureList items={p.items} />
                  <div className="flex-1" />
                  <p className="font-display text-2xl text-[#d4af37] mt-6">CUSTOM QUOTE</p>
                  <button type="button" onClick={() => onExplore(p.svc)} data-testid={`pkg-btn-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-4 w-full py-3 rounded border border-[#d4af37]/40 text-[#d4af37] font-bold uppercase tracking-wider text-xs hover:bg-[#d4af37] hover:text-black transition-colors">
                    Request Consultation
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Inquiry form */}
          <section id="inquiry" className="py-16 border-t border-[#d4af37]/10 scroll-mt-24" data-testid="ai-inquiry-section">
            <div className="max-w-3xl mx-auto rounded-2xl p-6 sm:p-10 bg-gradient-to-b from-[#14141a] to-[#0d0d10] border border-[#d4af37]/30">
              <div className="text-center mb-8">
                <span className="text-xs font-mono uppercase tracking-widest text-[#d4af37]">Free Consultation</span>
                <h2 className="font-display text-3xl uppercase text-white mt-2">Start Your AI Project</h2>
                <p className="text-xs text-zinc-500 mt-2">Tell us about your environment — we'll evaluate compatibility and respond with next steps.</p>
              </div>
              <InquiryForm service={service} setService={setService} />
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
